import * as React from 'react';
import { useRecoilValue } from 'recoil';
import {
  IRide,
  IRidePosition,
  TRideActiveLayer,
  IRideMapEcoDriveLines,
  IRideEcoDriveLinePaths,
} from '../types';
import { IS_ECO_DRIVE_LAYER } from '../constants';
import { activeRideNumberAtom, selectedRidesAtom } from '../RidePlayerState';
import { createEcoDriveLines, updateEcoDriveLines } from '../RidePlayerService';

interface IUseEcoDriveLine {
  ecoDriveLines: IRideMapEcoDriveLines;
  setEcoDriveLinesSize: (strokeWeight: number) => void;
  showAllEcoDriveLines: () => void;
  setEcoDriveLinesOpacity: (rideNumber: number, opacity: number) => void;
  hideAllEcoDriveLines: () => void;
  setAllEcoDriveLinesColor: (color: string, exceptRideNumber?: number) => void;
  createEcoDriveLines: () => void;
  removeAllEcoDriveLines: () => void;
  resetEcoDriveLinesColorForRide: (rideNumber: number) => void;
  resetAllEcoDriveLinesColor: () => void;
  setEcoDriveLineZIndex: (rideNumber: number, zIndex: number) => void;
  setAllEcoDriveLinesZIndex: (zIndex: number, exceptRideNumber?: number) => void;
}

/**
 * useEcoDriveLine custom hook
 * @param {google.maps.Map} map
 * @returns {IUseEcoDriveLine}
 */
const useEcoDriveLine = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  actualRidePosition?: IRidePosition | null
): IUseEcoDriveLine => {
  const ecoDriveLines = React.useRef<IRideMapEcoDriveLines>({});
  const ecoDrivePaths = React.useRef<IRideEcoDriveLinePaths>({});
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);

  /**
   * Set all ecoDrive lines color
   * @param {string} color
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllEcoDriveLinesColor = (color: string, exceptRideNumber?: number): void => {
    if (ecoDriveLines.current) {
      Object.keys(ecoDriveLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = ecoDriveLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ strokeColor: color });
        });
      });
    }
  };

  /**
   * Set ecoDrive lines stroke weight (aka size)
   * @param {number} strokeWeight
   * @returns {void}
   */
  const setEcoDriveLinesSize = (strokeWeight = 2): void => {
    Object.keys(ecoDriveLines.current).forEach((rideNumber: string) => {
      const polylines = ecoDriveLines[rideNumber];
      polylines.forEach((polyline: any) => {
        if (polyline.strokeWeight !== strokeWeight) {
          polyline.setOptions({ strokeWeight });
        }
      });
    });
  };

  /**
   * Show all ecoDrive lines
   * @returns {void}
   */
  const showAllEcoDriveLines = (): void => {
    Object.keys(ecoDriveLines.current).forEach((rideNumber: string) => {
      ecoDriveLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (!polyline.getMap() && selectedRides.includes(Number(rideNumber))) {
          polyline.setMap(map);
        }
      });
    });
  };

  /**
   * Hide all ecoDrive lines
   * @returns {void}
   */
  const hideAllEcoDriveLines = (): void => {
    Object.keys(ecoDriveLines.current).forEach((rideNumber: string) => {
      ecoDriveLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (polyline.getMap()) {
          polyline.setMap(null);
        }
      });
    });
  };

  /**
   * Set ecoDrive line opacity for specified ride
   * @param {number} rideNumber
   * @param {number} opacity
   */
  const setEcoDriveLinesOpacity = (rideNumber: number, opacity: number): void => {
    if (ecoDriveLines.current && ecoDriveLines.current[rideNumber]) {
      const polylines = ecoDriveLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.strokeOpacity) {
          polyline.setOptions({ strokeOpacity: opacity });
        }
      });
    }
  };

  /**
   * Reset eco drive lines color for specified ride
   * @param {number} rideNumber
   * @returns {void}
   */
  const resetEcoDriveLinesColorForRide = (rideNumber: number): void => {
    if (ecoDriveLines.current[rideNumber]) {
      const polylines: google.maps.Polyline[] = ecoDriveLines.current[rideNumber];
      polylines.forEach((polyline: google.maps.Polyline) => {
        const originalColor = polyline.get('originalColor');
        if (originalColor) {
          polyline.setOptions({ strokeColor: originalColor });
        }
      });
    }
  };

  /**
   * Reset eco drive lines color for all rides
   * @returns {void}
   */
  const resetAllEcoDriveLinesColor = (): void => {
    Object.keys(ecoDriveLines.current).forEach((rideNumber: string) => {
      const polylines: google.maps.Polyline[] = ecoDriveLines.current[rideNumber];
      polylines.forEach((polyline: google.maps.Polyline) => {
        const originalColor = polyline.get('originalColor');
        polyline.setOptions({ strokeColor: originalColor });
      });
    });
  };

  const removeAllEcoDriveLines = () => {
    hideAllEcoDriveLines();
    ecoDriveLines.current = {};
  };

  /**
   * Set eco drive line zindex for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setEcoDriveLineZIndex = (rideNumber: number, zIndex: number): void => {
    if (ecoDriveLines.current && ecoDriveLines.current[rideNumber]) {
      const polylines = ecoDriveLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.strokeOpacity) {
          polyline.setOptions({ zIndex });
        }
      });
    }
  };

  /**
   * Set all eco drive lines zIndex
   * @param {number} zIndex
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllEcoDriveLinesZIndex = (zIndex: number, exceptRideNumber?: number): void => {
    if (ecoDriveLines.current) {
      Object.keys(ecoDriveLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = ecoDriveLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ zIndex });
        });
      });
    }
  };

  // Handle show/hide ecodrive lines by selected rides
  React.useEffect(() => {
    Object.keys(ecoDriveLines.current).forEach((rideNumber: string) => {
      ecoDriveLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (!selectedRides.includes(Number(rideNumber)) && polyline.getMap()) {
          polyline.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !polyline.getMap() &&
          activeLayer === IS_ECO_DRIVE_LAYER
        ) {
          polyline.setMap(map);
        }
      });
    });
  }, [activeLayer, map, selectedRides]);

  const createRidePlayerEcoDriveLines = () => {
    let showOnMap = false;
    if (activeLayer === IS_ECO_DRIVE_LAYER) {
      showOnMap = true;
    }
    ecoDriveLines.current = createEcoDriveLines(
      showOnMap ? map : null,
      rides,
      ecoDrivePaths.current
    );
  };

  // Render ecoDrive lines
  React.useEffect(() => {
    createRidePlayerEcoDriveLines();
    // Unset ecoDrive lines from map
    return () => {
      hideAllEcoDriveLines();
      ecoDrivePaths.current = {};
      ecoDriveLines.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If actual position is present, update ecodrive lines
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      let showOnMap = false;
      if (activeLayer === IS_ECO_DRIVE_LAYER) {
        showOnMap = true;
      }
      updateEcoDriveLines(
        showOnMap ? map : null,
        rides,
        ecoDriveLines.current,
        ecoDrivePaths.current,
        activeRide,
        actualRidePosition
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualRidePosition, activeRide]);

  const data: IUseEcoDriveLine = {
    ecoDriveLines: ecoDriveLines.current,
    setEcoDriveLinesSize,
    showAllEcoDriveLines,
    setEcoDriveLinesOpacity,
    hideAllEcoDriveLines,
    setAllEcoDriveLinesColor,
    createEcoDriveLines: createRidePlayerEcoDriveLines,
    removeAllEcoDriveLines,
    resetEcoDriveLinesColorForRide,
    resetAllEcoDriveLinesColor,
    setEcoDriveLineZIndex,
    setAllEcoDriveLinesZIndex,
  };

  return data;
};

export default useEcoDriveLine;
